import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MatAutocomplete,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  merge,
  Observable,
  of,
  Subject
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import {
  CollectorEligibilityDraftRoster,
  CollectorEligibilityContext,
  CollectorUser,
  EligibleCollectorUser,
  MIN_COLLECTOR_SEARCH_QUERY_LENGTH
} from '../domain/user';
import { AlertService } from '../services/alert.service';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-collector-input',
  templateUrl: './collector-input.component.html',
  styleUrls: ['./collector-input.component.css']
})
export class CollectorInputComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Output() auth0IdChange = new EventEmitter<string>();

  @Input() set auth0Id(value: string) {
    const normalizedValue = value ?? '';
    if (normalizedValue === this.#auth0Id) {
      return;
    }
    this.#auth0Id = normalizedValue;
    this.syncDisplayValue();
  }

  get auth0Id(): string {
    return this.#auth0Id;
  }

  @Input() set displayName(value: string) {
    const normalizedValue = value ?? '';
    if (normalizedValue === this.#displayName) {
      return;
    }
    this.#displayName = normalizedValue;
    this.syncDisplayValue();
  }

  get displayName(): string {
    return this.#displayName;
  }

  @Input() set disabled(value: boolean) {
    this.#disabled = value;
    if (this.#disabled) {
      this.collectorControl.disable({ emitEvent: false });
    } else {
      this.collectorControl.enable({ emitEvent: false });
    }
  }

  get disabled(): boolean {
    return this.#disabled;
  }

  @Input() searchScope: 'collectors' | 'users' = 'collectors';
  @Input() allowForceSelection = false;

  @Input() set eligibilityContext(
    value: CollectorEligibilityContext | null | undefined
  ) {
    const nextValue = value ? { ...value } : null;
    if (this.isSameEligibilityContext(this.#eligibilityContext, nextValue)) {
      return;
    }
    this.#eligibilityContext = nextValue;
    this.eligibilityContext$.next(nextValue);
  }

  get eligibilityContext(): CollectorEligibilityContext | null {
    return this.#eligibilityContext;
  }

  #auth0Id = '';
  #displayName = '';
  #disabled = false;
  #eligibilityContext: CollectorEligibilityContext | null = null;

  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger?: MatAutocompleteTrigger;
  @ViewChild(MatAutocomplete) autocomplete?: MatAutocomplete;

  collectorControl = new FormControl<string | CollectorUser | null>(null, []);
  filteredUsers$: Observable<EligibleCollectorUser[]>;

  private readonly destroy$ = new Subject<void>();
  private readonly queryReset$ = new Subject<void>();
  private readonly eligibilityContext$ =
    new BehaviorSubject<CollectorEligibilityContext | null>(null);

  constructor(
    private userService: UserService,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    const query$ = merge(
      this.collectorControl.valueChanges.pipe(
        debounceTime(500),
        filter((value) => typeof value === 'string'),
        map((value: string) => value.trim())
      ),
      this.queryReset$.pipe(map(() => null))
    ).pipe(startWith(null), distinctUntilChanged());
    this.filteredUsers$ = combineLatest([
      query$,
      this.eligibilityContext$
    ]).pipe(
      switchMap(([value, eligibilityContext]) => {
        if (typeof value !== 'string') {
          return of([]);
        }
        const activeControlValue = this.collectorControl.value;
        if (
          typeof activeControlValue !== 'string' ||
          activeControlValue.trim() !== value
        ) {
          return of([]);
        }
        if (value.length < MIN_COLLECTOR_SEARCH_QUERY_LENGTH) {
          return of([]);
        }

        let search$: Observable<CollectorUser[]>;
        if (this.searchScope === 'users') {
          search$ = this.userService.searchUsersByName(value);
        } else if (eligibilityContext) {
          search$ = this.userService.searchEligibleCollectorsByName(
            value,
            eligibilityContext
          );
        } else {
          search$ = this.userService.searchCollectorsByName(value);
        }

        return search$.pipe(
          catchError((error) => {
            const validationMessage =
              this.getAssignmentValidationMessage(error);
            if (validationMessage) {
              this.alertService.showError(validationMessage);
            }
            return of([]);
          })
        );
      }),
      map((users) =>
        users.map((user) => ({
          ...user,
          eligible: user.eligible !== false,
          ineligibilityReasons: user.ineligibilityReasons ?? []
        }))
      )
    );
  }

  ngAfterViewInit() {
    this.autocomplete?.opened
      .pipe(
        switchMap(() => this.autocompleteTrigger?.panelClosingActions ?? EMPTY),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        if (!event) {
          this.resetPendingInput();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.queryReset$.complete();
  }

  clear() {
    if (this.disabled) {
      return;
    }

    this.#auth0Id = '';
    this.#displayName = '';
    this.setCommittedControlValue(null);
    this.auth0IdChange.emit('');
  }

  selectionChange(user: EligibleCollectorUser) {
    if (this.disabled || user?.eligible === false) {
      return;
    }

    this.selectUser(user);
  }

  forceSelection(user: EligibleCollectorUser, event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    if (
      this.disabled ||
      !this.allowForceSelection ||
      user?.eligible !== false
    ) {
      return;
    }

    this.selectUser(user);
    this.autocompleteTrigger?.closePanel();
  }

  preventForceButtonFocus(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  private selectUser(user: CollectorUser) {
    this.#auth0Id = user?.auth0Id ?? '';
    this.#displayName = user?.name ?? '';
    this.setCommittedControlValue(this.createSelectedUser());
    this.auth0IdChange.emit(this.#auth0Id);
  }

  displayUser(user: CollectorUser): string {
    return user?.name ?? '';
  }

  handleBlur() {
    if (this.autocompleteTrigger?.panelOpen) {
      return;
    }

    this.resetPendingInput();
  }

  private syncDisplayValue() {
    this.setCommittedControlValue(this.createSelectedUser());
  }

  private resetPendingInput() {
    const currentValue = this.getInputValue();
    const selectedValue = this.#displayName || this.#auth0Id;

    if (currentValue === selectedValue) {
      return;
    }

    if (!currentValue && !this.#auth0Id) {
      this.setCommittedControlValue(null);
      return;
    }

    this.clear();
  }

  private getInputValue(): string {
    const value = this.collectorControl.value;
    if (typeof value === 'string') {
      return value;
    }
    return value?.name ?? '';
  }

  private createSelectedUser(): CollectorUser | null {
    if (!this.#auth0Id) {
      return null;
    }
    return {
      auth0Id: this.#auth0Id,
      name: this.#displayName || this.#auth0Id
    };
  }

  private setCommittedControlValue(value: CollectorUser | null): void {
    this.collectorControl.setValue(value, { emitEvent: false });
    this.queryReset$.next();
  }

  private isSameEligibilityContext(
    left: CollectorEligibilityContext | null,
    right: CollectorEligibilityContext | null
  ): boolean {
    return (
      left?.gameId === right?.gameId &&
      left?.eventCode === right?.eventCode &&
      left?.eventId === right?.eventId &&
      left?.dcDate === right?.dcDate &&
      left?.dcTime === right?.dcTime &&
      left?.dcType === right?.dcType &&
      left?.dcSubtype === right?.dcSubtype &&
      left?.dcTypeOverride === right?.dcTypeOverride &&
      left?.gmTeamLeaderAuthId === right?.gmTeamLeaderAuthId &&
      left?.currentEventIndex === right?.currentEventIndex &&
      this.isSameDraftRoster(left?.draftRoster, right?.draftRoster)
    );
  }

  private isSameDraftRoster(
    left: CollectorEligibilityDraftRoster | undefined,
    right: CollectorEligibilityDraftRoster | undefined
  ): boolean {
    if (left === right) {
      return true;
    }
    if (
      !left ||
      !right ||
      left.gameId !== right.gameId ||
      left.dcDate !== right.dcDate ||
      left.dcTime !== right.dcTime ||
      left.dcType !== right.dcType ||
      left.dcSubtype !== right.dcSubtype ||
      left.collectionEvents.length !== right.collectionEvents.length
    ) {
      return false;
    }

    return left.collectionEvents.every((leftEvent, index) => {
      const rightEvent = right.collectionEvents[index];
      return (
        leftEvent._id === rightEvent?._id &&
        leftEvent.code === rightEvent?.code &&
        leftEvent.collectorAuthId === rightEvent?.collectorAuthId &&
        leftEvent.dcTypeOverride === rightEvent?.dcTypeOverride &&
        leftEvent.configSource?.dcType === rightEvent?.configSource?.dcType &&
        leftEvent.configSource?.dcSubtype ===
          rightEvent?.configSource?.dcSubtype &&
        leftEvent.status === rightEvent?.status
      );
    });
  }

  private getAssignmentValidationMessage(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) && typeof error !== 'object') {
      return null;
    }

    const status = (error as HttpErrorResponse)?.status;
    if (status !== 400 && status !== 422 && status !== 442) {
      return null;
    }

    return (
      (error as HttpErrorResponse)?.error?.message ??
      'Collector assignment could not be validated'
    );
  }
}
